import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/* /smartket/how-it-works — Orientation page.
   Two lanes side-by-side (Founder + Marketer), each 4 numbered steps,
   plain English. Ends with a "what happens when" trust panel. */

const FOUNDER_STEPS = [
  { n: "1", t: "List your product", d: "Give it a name, a link, a target audience, and the commission % you'll pay marketers on each verified sale. Pick a template (Sprint / Steady / Partnership) that decides how long a slip stays active. Free to list — no monthly cost." },
  { n: "2", t: "Connect Stripe", d: "One-click Express onboarding. Stripe verifies your identity, handles taxes, and becomes the safe rails for every transaction. Takes ~5 minutes if it's your first time." },
  { n: "3", t: "Wait for a slip pull", d: "Marketers browse the board and 'pull a slip' on your listing. You get notified. You review their profile (niches, reach, past wins) and either counter-sign to activate, or decline. No obligation until you sign." },
  { n: "4", t: "Get sales, pay commissions", d: "Every purchase through the marketer's tracked link auto-attributes. Stripe processes the charge, we hold the marketer's commission until verified, then it releases automatically. You keep the rest. Zero platform fee at launch." },
];

const MARKETER_STEPS = [
  { n: "1", t: "Build your profile", d: "Name, firm (if any), niches you crush, your reach (followers / list size / any real number), past wins. This is what founders read before agreeing to work with you. Be real, be short." },
  { n: "2", t: "Connect Stripe payouts", d: "One-click Express onboarding. Payout-only account — you don't need a merchant account. Stripe verifies you and creates your payout channel. ~3 minutes." },
  { n: "3", t: "Pull a slip", d: "Browse the Product Board, find a product you can actually move, click 'Pull a Slip'. Sign the terms (60-day attribution window, template terms, anti-ghosting protection). Founder counter-signs to activate. Now the deal is live." },
  { n: "4", t: "Promote and get paid", d: "Share your unique attribution link (SK-XXXXXXXX). Every sale within 60 days of a click gets credited to you. FIRST verified sale pays you immediately (yes, immediately). After that, bi-weekly Stripe payouts land in your account automatically." },
];

const TRUST_POINTS = [
  { emoji: "⚡", h: "First sale pays you immediately", d: "Not weekly. Not net-30. The second your first sale hits, your commission is on its way. That's the promise." },
  { emoji: "🔒", h: "Signed contracts, not handshakes", d: "Every slip is a real digital contract with agreed terms — commission %, duration, attribution window. Both sides sign. We hold the record." },
  { emoji: "🛡️", h: "Anti-ghosting protection", d: "If a founder cancels a slip that's generating over $1K/month, they owe 60 days notice + a 3-month buyout. Your work is protected." },
  { emoji: "🌾", h: "Money grows on FREE", d: "0% platform fee at launch. Founders keep everything after Stripe fees and the marketer's cut. Marketers keep 100% of their commission." },
];

export default function SmartKetHowItWorks() {
  useEffect(() => {
    const prev = document.title;
    document.title = "SmartKet — How It Works";
    return () => { document.title = prev; };
  }, []);

  return (
    <div data-testid="smartket-how-it-works" className="min-h-screen bg-[#0d1410] text-white">
      <div className="border-b-2 border-[#d4a34a]/40 bg-[#141a15]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          <Link to="/smartket" className="font-mono text-[10px] tracking-[0.4em] text-white/60 hover:text-[#d4a34a]">
            ← SMARTKET
          </Link>
          <h1 className="mt-3 font-display text-4xl md:text-6xl leading-none tracking-tight">
            HOW IT <span className="text-[#d4a34a]">WORKS</span>.
          </h1>
          <p className="mt-3 font-mono text-sm md:text-base text-white/80 max-w-2xl leading-relaxed">
            No jargon. No maze. Two lanes below — founder on the left, marketer on the right. Four steps each. That's the whole product.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* FOUNDER LANE */}
        <section data-testid="hiw-founder-lane" className="border-2 border-white/15 bg-gradient-to-br from-[#1a3a2a] to-[#0d1410] p-6">
          <div className="font-mono text-[10px] tracking-[0.4em] text-white/60 mb-2">▸ IF YOU HAVE A PRODUCT</div>
          <div className="font-display text-3xl md:text-4xl leading-none mb-6">FOUNDER PATH</div>
          <div className="space-y-5">
            {FOUNDER_STEPS.map(s => (
              <div key={s.n} data-testid={`hiw-founder-step-${s.n}`} className="flex gap-3">
                <div className="font-display text-4xl leading-none text-[#d4a34a] w-10 flex-shrink-0">{s.n}</div>
                <div>
                  <div className="font-display text-lg md:text-xl leading-tight mb-1">{s.t}</div>
                  <div className="font-mono text-xs md:text-sm text-white/80 leading-relaxed">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/smartket/join" data-testid="hiw-founder-cta"
            className="mt-6 block bg-white text-[#1a3a2a] font-display text-base tracking-widest text-center px-4 py-3 shadow-brutal hover:bg-[#d4a34a] transition-colors">
            START AS A FOUNDER ▸
          </Link>
        </section>

        {/* MARKETER LANE */}
        <section data-testid="hiw-marketer-lane" className="border-2 border-white/15 bg-gradient-to-br from-[#c98a3a] to-[#3a2410] p-6">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#2a1a08] mb-2">IF YOU CAN MARKET ◂</div>
          <div className="font-display text-3xl md:text-4xl leading-none mb-6 text-[#2a1a08]">MARKETER PATH</div>
          <div className="space-y-5">
            {MARKETER_STEPS.map(s => (
              <div key={s.n} data-testid={`hiw-marketer-step-${s.n}`} className="flex gap-3">
                <div className="font-display text-4xl leading-none text-[#2a1a08] w-10 flex-shrink-0">{s.n}</div>
                <div>
                  <div className="font-display text-lg md:text-xl leading-tight mb-1 text-[#2a1a08]">{s.t}</div>
                  <div className="font-mono text-xs md:text-sm text-[#2a1a08]/90 leading-relaxed">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/smartket/join" data-testid="hiw-marketer-cta"
            className="mt-6 block bg-[#2a1a08] text-white font-display text-base tracking-widest text-center px-4 py-3 shadow-brutal hover:bg-black transition-colors">
            ◂ START AS A MARKETER
          </Link>
        </section>
      </div>

      {/* TRUST PANEL */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-12">
        <div className="border-2 border-[#d4a34a]/40 bg-[#141a15] p-6">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#d4a34a] mb-4">WHAT MAKES SMARTKET DIFFERENT</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TRUST_POINTS.map(t => (
              <div key={t.h} data-testid={`hiw-trust-${t.h.slice(0,8)}`} className="flex gap-3 bg-black/30 p-3 border border-white/10">
                <div className="text-2xl flex-shrink-0">{t.emoji}</div>
                <div>
                  <div className="font-display text-base md:text-lg leading-tight mb-1 text-[#d4a34a]">{t.h}</div>
                  <div className="font-mono text-xs text-white/80 leading-relaxed">{t.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 border border-white/10 bg-[#141a15] p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[#d4a34a] mb-1">READY?</div>
            <div className="font-mono text-sm text-white/80">Both doors are open. Pick your lane and walk through.</div>
          </div>
          <Link to="/smartket/join" data-testid="hiw-final-cta"
            className="bg-[#d4a34a] text-black font-display text-sm tracking-widest px-4 py-3 shadow-brutal hover:bg-white transition-colors">
            OPEN A DOOR ▸
          </Link>
        </div>

        <div className="mt-4 font-mono text-[10px] text-white/40 text-center">
          Questions? Read the <Link to="/smartket/terms" className="text-[#d4a34a] underline">full Slip Agreement</Link> — plain English on top, full legal below.
        </div>
      </div>
    </div>
  );
}
